import { useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TerrainIcon from '@mui/icons-material/Terrain';
import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';

// A stone name on a packing list is `{ name }` when it was typed, or `{ productId, code, name, nameAr, nameFa }` when
// it was picked from Inventory. The server re-reads every picked product and keeps the link only when it is a live
// product of the list's own branch, so what is held here is only what the form shows until Save.

const baseName = (s) => String(typeof s === 'string' ? s : (s && (s.name || s.code)) || '').replace(/\s+/g, ' ').trim();

/** A stone name as the person reads it: a picked product's Arabic / Persian name when the app is in that language. */
export function stoneLabel(stone, lang) {
  if (!stone) return '';
  if (typeof stone === 'string') return stone.trim();
  const localized = lang === 'ar' ? stone.nameAr : lang === 'fa' ? stone.nameFa : '';
  return String(localized || stone.name || stone.code || '').trim();
}

/** Typed strings become `{ name }`; blanks and repeats (case and spacing aside - the server's rule too) are dropped. */
export function tidyStones(list) {
  const out = [];
  const seen = new Set();
  for (const item of list || []) {
    const stone = typeof item === 'string' ? { name: item } : item;
    const name = baseName(stone);
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push(stone && stone.productId ? { ...stone, name } : { name });
  }
  return out;
}

const sameStone = (a, b) => (a.productId && b.productId
  ? String(a.productId) === String(b.productId)
  : baseName(a).toLowerCase() === baseName(b).toLowerCase());

const asOption = (p) => ({
  productId: p._id, code: p.code || '', name: p.name || p.code || '', nameAr: p.nameAr || '', nameFa: p.nameFa || '',
});

// One or more stone names: pick them from this branch's Inventory, or just type one and press Enter (a name typed and
// left in the box when the field loses focus is added too, so it is never silently lost). Both kinds print on the list.
export default function StoneNamesField({ value, onChange, branchId, T, size = 'small', touch = false }) {
  const { t, i18n } = useTranslation();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);

  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  // The products of the list's branch matching what is typed (nothing typed: the first few, by name).
  useEffect(() => {
    if (!branchId) { setOptions([]); return undefined; }
    const mine = ++seq.current;
    setLoading(true);
    const timer = setTimeout(() => {
      authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/products-lookup`,
        params: { search: input.trim(), branchId },
      }).then((res) => { if (mine === seq.current) setOptions((Array.isArray(res.data) ? res.data : []).map(asOption)); })
        .catch(() => { if (mine === seq.current) setOptions([]); })
        .finally(() => { if (mine === seq.current) setLoading(false); });
    }, input.trim() ? 250 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, branchId]);

  return (
    <Autocomplete
      multiple freeSolo autoSelect fullWidth
      options={options}
      value={value}
      inputValue={input}
      loading={loading}
      onInputChange={(_, v, reason) => setInput(reason === 'reset' ? '' : v)}
      onChange={(_, next) => onChange(tidyStones(next))}
      filterOptions={(x) => x}   // the server already filtered (by name or code) - don't filter again here
      filterSelectedOptions
      isOptionEqualToValue={sameStone}
      getOptionLabel={(o) => stoneLabel(o, i18n.language)}
      // a menu belongs above whatever surface opened it (see the portal z-index note in CLAUDE.md)
      slotProps={{ popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } } }}
      // on a phone: a clear button a finger can hit (the default is ~26px)
      sx={touch ? { '& .MuiAutocomplete-clearIndicator': { width: 36, height: 36, mr: -0.5 } } : undefined}
      renderOption={(props, option) => (
        <li {...props} key={String(option.productId)}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: touch ? '0.95rem' : '0.82rem', fontWeight: 600 }}>{stoneLabel(option, i18n.language)}</Typography>
            {option.code && (
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontFamily: 'monospace' }}>{option.code}</Typography>
            )}
          </Box>
        </li>
      )}
      renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
        <Chip {...getTagProps({ index })} key={`${baseName(option).toLowerCase()}-${index}`} size="small"
          icon={option.productId ? <TerrainIcon sx={{ fontSize: 13 }} /> : undefined}
          label={stoneLabel(option, i18n.language)}
          sx={{ height: touch ? 32 : 24, fontSize: touch ? '0.85rem' : '0.74rem', bgcolor: T ? T.CTRL_BG : undefined,
            ...(touch ? { '& .MuiChip-deleteIcon': { fontSize: 20, boxSizing: 'content-box', p: '6px', marginInline: '-12px -2px' } } : {}) }} />
      ))}
      renderInput={(params) => (
        <TextField {...params} size={size} InputLabelProps={{ shrink: true }}
          label={t('mis.plStoneNamesLabel')}
          placeholder={value.length ? '' : t('mis.plStoneNamesPlaceholder')}
          helperText={t('mis.plStoneNamesHelp')}
          inputProps={{ ...params.inputProps, 'data-testid': 'pl-stone-names', enterKeyHint: 'done' }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={13} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }} />
      )}
    />
  );
}
